import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  User,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  Calendar,
  Award,
  FileText,
  Mail,
  Phone,
  MapPin,
  Clock,
  AlertCircle,
  Search,
  Filter,
  File,
  Image,
  Video,
  Music,
  Paperclip,
  ExternalLink
} from 'lucide-react';

interface PendingDeveloper {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  bio: string;
  skills: string[];
  experience: string;
  hourlyRate: {
    amount: number;
    currency: string;
  };
  availability: string;
  education: {
    id: string;
    institution: string;
    degree: string;
    fieldOfStudy: string;
    startMonth: string;
    startYear: string;
    endMonth: string;
    endYear: string;
    isCompleted: boolean;
  }[];
  certifications: {
    id: string;
    name: string;
    issuingOrganization: string;
    issueDate: string;
    expiryDate: string;
    credentialId: string;
  }[];
  documents: {
    id: string;
    name: string;
    type: string;
    url: string;
    originalUrl?: string;
    publicId?: string;
    resourceType?: string;
    format?: string;
    uploadedAt?: string;
    size?: number;
    mimetype?: string;
  }[];
  status: string;
  createdAt: string;
  approved?: boolean;
  isEmailVerified: boolean;
  approvalDetails?: {
    status: string;
    approvedBy?: string;
    approvedAt?: string;
    notes?: string;
  };
}

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

// FIXED: Enhanced File Preview Component with proper Cloudinary URL handling
const FilePreview: React.FC<{ document: any }> = ({ document: doc }) => {
  const getFileIcon = (fileName: string, mimetype?: string) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    
    if (mimetype?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    }
    if (mimetype?.startsWith('video/') || ['mp4', 'avi', 'mov', 'wmv'].includes(extension || '')) {
      return <Video className="w-5 h-5 text-purple-500" />;
    }
    if (mimetype?.startsWith('audio/') || ['mp3', 'wav', 'flac', 'aac'].includes(extension || '')) {
      return <Music className="w-5 h-5 text-green-500" />;
    }
    if (['pdf'].includes(extension || '')) {
      return <FileText className="w-5 h-5 text-red-500" />;
    }
    if (['doc', 'docx', 'txt'].includes(extension || '')) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'resume': 'Resume',
      'certificate': 'Certificate',
      'portfolio': 'Portfolio',
      'cover_letter': 'Cover Letter',
      'other': 'Document'
    };
    return typeMap[type] || 'Document';
  };

  // FIXED: Enhanced download function with proper Cloudinary URL handling
  const handleDownload = async () => {
    if (!doc.url) {
      alert('No download URL available for this file.');
      return;
    }

    try {
      // FIXED: For Cloudinary URLs, construct proper download URL
      let downloadUrl = doc.url;
      
      if (doc.url.includes('cloudinary.com')) {
        // Parse the Cloudinary URL to get the correct download format
        const urlParts: string[] = doc.url.split('/');
        
        // FIXED: Use traditional for loop instead of findIndex to avoid TypeScript errors
        let uploadIndex = -1;
        for (let i = 0; i < urlParts.length; i++) {
          if (urlParts[i] === 'upload') {
            uploadIndex = i;
            break;
          }
        }
        
        if (uploadIndex > -1) {
          // Get resource type from URL
          const resourceType = urlParts[uploadIndex - 1];
          
          // FIXED: Handle different resource types properly
          if (resourceType === 'raw') {
            // For raw files (documents), use fl_attachment
            if (!doc.url.includes('fl_attachment')) {
              const separator = doc.url.includes('?') ? '&' : '?';
              downloadUrl = `${doc.url}${separator}fl_attachment`;
            }
          } else if (resourceType === 'image') {
            // For images uploaded as documents, convert to raw URL
            const publicIdWithFormat = urlParts.slice(uploadIndex + 1).join('/');
            // Remove version if present
            const cleanPublicId = publicIdWithFormat.replace(/^v\d+\//, '');
            
            // Construct raw URL for download
            const baseUrl = urlParts.slice(0, uploadIndex - 1).join('/');
            downloadUrl = `${baseUrl}/raw/upload/fl_attachment/${cleanPublicId}`;
          } else {
            // For video/audio, add fl_attachment
            if (!doc.url.includes('fl_attachment')) {
              const separator = doc.url.includes('?') ? '&' : '?';
              downloadUrl = `${doc.url}${separator}fl_attachment`;
            }
          }
        }
      }
      // Method 1: Try fetch with blob
      try {
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Accept': '*/*',
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = doc.name || 'document';
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL
          window.URL.revokeObjectURL(url);
          return;
        } else {
        }
      } catch (fetchError) {
      }

      // Method 2: Direct link download
      try {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = doc.name || 'document';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      } catch (linkError) {
      }

      // Method 3: Open in new tab as fallback
      try {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      } catch (openError) {
        alert('Unable to download file. The file may not be accessible or the URL may be invalid.');
      }

    } catch (error) {
      alert('An error occurred while trying to download the file.');
    }
  };

  // FIXED: Add view/preview function for supported file types
  const handleView = () => {
    if (doc.originalUrl || doc.url) {
      const viewUrl = doc.originalUrl || doc.url;
      window.open(viewUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const canPreview = (fileName: string, mimetype?: string) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    return (
      mimetype?.startsWith('image/') ||
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'].includes(extension || '') ||
      mimetype === 'application/pdf'
    );
  };

  return (
    <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
      <div className="flex-shrink-0">
        {getFileIcon(doc.name, doc.mimetype)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {doc.name}
        </p>
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <span>{getFileTypeLabel(doc.type)}</span>
          {doc.size && (
            <>
              <span>•</span>
              <span>{formatFileSize(doc.size)}</span>
            </>
          )}
          {doc.uploadedAt && (
            <>
              <span>•</span>
              <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
            </>
          )}
          {doc.resourceType && (
            <>
              <span>•</span>
              <span className="capitalize">{doc.resourceType}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {/* FIXED: Add preview button for supported file types */}
        {canPreview(doc.name, doc.mimetype) && (
          <button
            onClick={handleView}
            className="flex-shrink-0 text-green-500 hover:text-green-600 transition-colors p-1 rounded hover:bg-green-50"
            title="Preview file"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
        
        {/* Download button */}
        {doc.url && (
          <button
            onClick={handleDownload}
            className="flex-shrink-0 text-blue-500 hover:text-blue-600 transition-colors p-1 rounded hover:bg-blue-50"
            title="Download file"
          >
            <Download className="w-4 h-4" />
          </button>
        )}
        
        {/* External link button as backup */}
        {doc.url && (
          <button
            onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
            className="flex-shrink-0 text-gray-500 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-50"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const DeveloperApprovals: React.FC = () => {
  const { user } = useAuth();
  const [pendingDevelopers, setPendingDevelopers] = useState<PendingDeveloper[]>([]);
  const [selectedDeveloper, setSelectedDeveloper] = useState<PendingDeveloper | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [error, setError] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [processingApproval, setProcessingApproval] = useState(false);

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    fetchPendingDevelopers();
  }, []);

  const fetchPendingDevelopers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/admin/developers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter to show only email verified developers
        const verifiedDevelopers = (data.developers || []).filter((dev: PendingDeveloper) => dev.isEmailVerified);
        setPendingDevelopers(verifiedDevelopers);
      } else {
        throw new Error('Failed to fetch developers');
      }
    } catch (error) {
      setError('Failed to load pending developers');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine the actual approval status
  const getDeveloperStatus = (developer: PendingDeveloper) => {
    // Check approvalDetails.status first (most reliable)
    if (developer.approvalDetails?.status) {
      return developer.approvalDetails.status;
    }
    
    // Fallback to approved field
    if (developer.approved === true) {
      return 'approved';
    } else if (developer.approved === false) {
      // If approved is explicitly false, check if it's pending or rejected
      // If no approvalDetails, assume it's pending
      return 'pending';
    }
    
    // Default to pending for new registrations
    return 'pending';
  };

  const handleApproval = async (developerId: string, approved: boolean) => {
    setProcessingApproval(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/admin/approve-developer/${developerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          approved,
          notes: approvalNotes
        })
      });

      if (response.ok) {
        await fetchPendingDevelopers();
        setSelectedDeveloper(null);
        setApprovalNotes('');
        setError('');
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to process approval');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process approval');
    } finally {
      setProcessingApproval(false);
    }
  };

  const getExperienceColor = (experience: string) => {
    switch (experience) {
      case 'junior': return 'text-green-600 bg-green-100';
      case 'mid': return 'text-blue-600 bg-blue-100';
      case 'senior': return 'text-purple-600 bg-purple-100';
      case 'expert': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'busy': return 'text-yellow-600 bg-yellow-100';
      case 'unavailable': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const filteredDevelopers = pendingDevelopers.filter(dev => {
    const matchesSearch = `${dev.firstName} ${dev.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dev.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const developerStatus = getDeveloperStatus(dev);
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'pending_approval' && developerStatus === 'pending') ||
      (statusFilter === 'approved' && developerStatus === 'approved') ||
      (statusFilter === 'rejected' && developerStatus === 'rejected');
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-sm border animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Developer Approvals</h1>
          <p className="text-gray-600 mt-1">Review and approve developer registrations</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
            {filteredDevelopers.filter(d => getDeveloperStatus(d) === 'pending').length} Pending
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search developers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Developers List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {filteredDevelopers.length === 0 ? (
            <div className="text-center py-12">
              <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No developers found</h3>
              <p className="text-gray-600">No developers match your current filters.</p>
            </div>
          ) : (
            filteredDevelopers.map((developer) => {
              const developerStatus = getDeveloperStatus(developer);
              return (
                <div
                  key={developer._id}
                  className={`bg-white rounded-lg shadow-sm border p-6 cursor-pointer transition-all duration-200 ${
                    selectedDeveloper?._id === developer._id ? 'ring-2 ring-blue-500 border-blue-300' : 'hover:shadow-md'
                  }`}
                  onClick={() => setSelectedDeveloper(developer)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">
                          {developer.firstName[0]}{developer.lastName[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {developer.firstName} {developer.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">{developer.email}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getExperienceColor(developer.experience)}`}
                      >
                        {developer.experience}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(developerStatus)}`}
                      >
                        {getStatusLabel(developerStatus)}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{developer.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">Applied {getRelativeTime(developer.createdAt)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Award className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {formatCurrency(developer.hourlyRate.amount, developer.hourlyRate.currency)}/hour
                      </span>
                    </div>
                    {developer.documents && developer.documents.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{developer.documents.length} document(s)</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <div className="flex flex-wrap gap-1">
                      {developer.skills.slice(0, 3).map((skill, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                      {developer.skills.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{developer.skills.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Developer Details */}
        <div className="lg:sticky lg:top-6">
          {selectedDeveloper ? (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Developer Details</h2>
                  {(() => {
                    const status = getDeveloperStatus(selectedDeveloper);
                    if (status === 'pending') {
                      return (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleApproval(selectedDeveloper._id, false)}
                            disabled={processingApproval}
                            className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            {processingApproval ? 'Processing...' : 'Reject'}
                          </button>
                          <button
                            onClick={() => handleApproval(selectedDeveloper._id, true)}
                            disabled={processingApproval}
                            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {processingApproval ? 'Processing...' : 'Approve'}
                          </button>
                        </div>
                      );
                    } else if (status === 'approved') {
                      return (
                        <div className="flex items-center space-x-2">
                          <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
                            Approved
                          </span>
                          <button
                            onClick={() => handleApproval(selectedDeveloper._id, false)}
                            disabled={processingApproval}
                            className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            {processingApproval ? 'Processing...' : 'Reject'}
                          </button>
                        </div>
                      );
                    } else if (status === 'rejected') {
                      return (
                        <div className="flex items-center space-x-2">
                          <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800">
                            Rejected
                          </span>
                          <button
                            onClick={() => handleApproval(selectedDeveloper._id, true)}
                            disabled={processingApproval}
                            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {processingApproval ? 'Processing...' : 'Approve'}
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Basic Information */}
                <div>
                  <h3 className="text-md font-semibold text-gray-900 mb-3">Basic Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {selectedDeveloper.firstName} {selectedDeveloper.lastName}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{selectedDeveloper.email}</span>
                    </div>
                    {selectedDeveloper.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{selectedDeveloper.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{selectedDeveloper.location}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Applied {getRelativeTime(selectedDeveloper.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div>
                  <h3 className="text-md font-semibold text-gray-900 mb-3">Professional Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Experience Level:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getExperienceColor(selectedDeveloper.experience)}`}>
                        {selectedDeveloper.experience}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Hourly Rate:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(selectedDeveloper.hourlyRate.amount, selectedDeveloper.hourlyRate.currency)}/hour
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Availability:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getAvailabilityColor(selectedDeveloper.availability)}`}>
                        {selectedDeveloper.availability}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {selectedDeveloper.bio && (
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-3">Bio</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{selectedDeveloper.bio}</p>
                  </div>
                )}

                {/* Skills */}
                {selectedDeveloper.skills && selectedDeveloper.skills.length > 0 && (
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-3">Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedDeveloper.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {selectedDeveloper.education && selectedDeveloper.education.length > 0 && (
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-3">Education</h3>
                    <div className="space-y-3">
                      {selectedDeveloper.education.map((edu, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900">{edu.degree} in {edu.fieldOfStudy}</div>
                          <div className="text-sm text-gray-600">{edu.institution}</div>
                          <div className="text-xs text-gray-500">
                            {edu.startMonth} {edu.startYear} - {edu.isCompleted ? `${edu.endMonth} ${edu.endYear}` : 'Present'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certifications */}
                {selectedDeveloper.certifications && selectedDeveloper.certifications.length > 0 && (
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-3">Certifications</h3>
                    <div className="space-y-3">
                      {selectedDeveloper.certifications.map((cert, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900">{cert.name}</div>
                          <div className="text-sm text-gray-600">{cert.issuingOrganization}</div>
                          <div className="text-xs text-gray-500">
                            Issued: {new Date(cert.issueDate).toLocaleDateString()}
                            {cert.expiryDate && ` • Expires: ${new Date(cert.expiryDate).toLocaleDateString()}`}
                          </div>
                          {cert.credentialId && (
                            <div className="text-xs text-gray-500 mt-1">
                              Credential ID: {cert.credentialId}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents - FIXED */}
                {selectedDeveloper.documents && selectedDeveloper.documents.length > 0 && (
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-3">
                      Documents ({selectedDeveloper.documents.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedDeveloper.documents.map((doc, index) => (
                        <FilePreview key={`${doc.id || index}-${doc.name}`} document={doc} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Approval Notes */}
                <div>
                  <h3 className="text-md font-semibold text-gray-900 mb-3">Approval Notes</h3>
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Add notes about this developer (optional)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
              <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Developer</h3>
              <p className="text-gray-600">Choose a developer from the list to view their details and documents.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeveloperApprovals;

